import { useEffect, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
// One icon per import, pulled in only where it is actually used. lucide-react's
// named exports are the per-icon entry points — Vite tree-shakes the rest of the
// 2000-icon set out of the bundle.
import { CakeSlice } from 'lucide-react'
import { ClipboardList } from 'lucide-react'
import { CalendarClock } from 'lucide-react'
import { CalendarX2 } from 'lucide-react'
import { CircleCheck } from 'lucide-react'
import { CircleDollarSign } from 'lucide-react'
import { CircleUser } from 'lucide-react'
import { ClipboardCopy } from 'lucide-react'
import { FileText } from 'lucide-react'
import { Loader2 } from 'lucide-react'
import { LogIn } from 'lucide-react'
import { Mail } from 'lucide-react'
import { MessageSquare } from 'lucide-react'
import { NotebookPen } from 'lucide-react'
import { ClipboardPen } from 'lucide-react'
import { Phone } from 'lucide-react'
import { Receipt } from 'lucide-react'
import { SquareArrowOutUpRight } from 'lucide-react'
import { CircleDollarSignOff } from '@/components/icons/CircleDollarSignOff'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  getCustomer,
  getBooking,
  getCustomerNotes,
  cancelBooking,
  issueBookingDocument,
  type CustomerDetail,
  type CustomerNote,
} from '@/lib/api'
import { useCopiedClient } from '@/contexts/CopiedClientContext'
import { useRole } from '@/hooks/useRole'
import { PaymentDialog } from '@/components/payments/PaymentDialog'
import { CheckInDialog } from '@/components/calendar/CheckInDialog'
import { CheckOutDialog } from '@/components/calendar/CheckOutDialog'
import { ClientNotesDialog } from '@/components/calendar/ClientNotesDialog'
import { IntakeDialog } from '@/components/calendar/IntakeDialog'
import { TreatmentCardDialog } from '@/components/calendar/TreatmentCardDialog'
import { RescheduleDialog } from '@/components/calendar/RescheduleDialog'
import { cardSummary } from '@/lib/treatmentCard'
import { attendanceState, type CalendarEvent } from '@/lib/render'
import { birthdayLabel, todayLocalDate } from '@/lib/calendar'
import { canDragBooking, minutesFromLocalTime, minutesToLabel } from '@/lib/slotBooking'
import { cn } from '@/lib/utils'
import { notePlainText } from '@/components/data/NoteBody'
import { durationMins, formatTime, tileLinkProps, useOpenBooking, type TileMoveGeometry } from './shared'
import { useTileDrag } from './useTileDrag'

// Re-exported for callers/tests that reach the handoff through this component;
// the implementation lives in lib/settleHandoff.ts next to its collapse helpers.
export { buildSettleHandoff } from '@/lib/settleHandoff'

// ─── The booking sheet ─────────────────────────────────────────────────────────
//
// Tapping an appointment opens a sheet: who it is, what they hold, how to reach
// them, and what can be done. Requested 2026-08-17 — it replaces the six-item
// dropdown that stood here, which could show a verb but never a fact.
//
// The app is chat-first and its data screens are read-only; this is a sanctioned
// exception (CLAUDE.md → "What Not to Build"). What keeps it from becoming the
// form-based write UI the project bans is unchanged from the dropdown it grew
// out of: EVERY ITEM IS A WHOLE DECISION, and the operator never types a value
// that matters. The three items that need more than a verb open their own dialog
// and each holds the line its own way —
//
//   Check in  — the credit to spend is a CHOICE from the customer's own packages,
//               shown before anything is burned. Never an amount.
//   Check out — WHICH services were delivered, ticked. The finishing time is the
//               moment of pressing, so there is no time field.
//   Move      — the day and the time are both real open slots from availability.
//
// …and the two free-text boxes in the whole sheet (a cancellation reason, a
// reschedule reason) are optional, are stored verbatim, and are read by nobody as
// data. Anything needing a typed value that MATTERS — an amount, a refund, a
// price correction, a no-show — is still handed to chat.
//
// ATTENDANCE IS TWO TIMESTAMPS, NOT TWO STATUSES. `booked` covers "not arrived"
// and "in session" alike; `attendanceState` is the only thing that should be
// asked which. See lib/render.ts for why the status vocabulary was left alone.

export interface BookingActionsProps {
  event: CalendarEvent
  /** Refetch the calendar after a mutation lands. */
  onChanged?: () => void
  /** The tile. Rendered inside the sheet trigger. */
  children: ReactNode
  /** Positioning/appearance of the tile — moved onto the trigger so layout is unchanged. */
  className?: string
  style?: CSSProperties
  /**
   * Drag-to-move plumbing from `useTileDrag`, spread onto the trigger button.
   * Absent on every surface that cannot move an appointment.
   *
   * `onClickCapture` is not optional decoration: it swallows the click a browser
   * fires after a drag's pointerup, and without it every drop would also open
   * this sheet behind the move dialog.
   */
  dragProps?: {
    onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void
    onClickCapture: (event: { preventDefault: () => void; stopPropagation: () => void }) => void
  }
}

function errorReason(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e)
  try {
    const parsed = JSON.parse(raw) as { error?: { reason?: string; message?: string; current_status?: string } }
    return parsed.error?.message ?? parsed.error?.reason ?? raw
  } catch {
    return raw
  }
}

/** One row in the sheet's action list. A whole decision, never a field. */
function ActionRow({
  icon,
  label,
  hint,
  tone = 'default',
  disabled,
  busy,
  onClick,
}: {
  icon: ReactNode
  label: string
  hint?: string
  tone?: 'default' | 'destructive'
  disabled?: boolean
  busy?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled || busy}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-45',
        tone === 'destructive'
          ? 'border-border bg-card hover:border-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/40'
          : 'border-border bg-card hover:bg-muted',
      )}
    >
      <span className="shrink-0" aria-hidden>
        {busy ? <Loader2 className="size-4 animate-spin" /> : icon}
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block truncate text-[13.5px] font-semibold',
            tone === 'destructive' ? 'text-rose-700 dark:text-rose-400' : 'text-foreground',
          )}
        >
          {label}
        </span>
        {hint ? (
          <span className="mt-px block truncate text-[11.5px] text-muted-foreground">{hint}</span>
        ) : null}
      </span>
    </button>
  )
}

/** tel: / sms: / mailto: — the three ways to reach somebody, or nothing. */
function ContactRow({ phone, email }: { phone: string | null; email: string | null }) {
  if (!phone && !email) {
    return (
      <p className="text-[11.5px] text-muted-foreground">
        No phone or email on file — add one in chat.
      </p>
    )
  }
  const linkClass =
    'flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border bg-card px-2 py-1.5 text-[12.5px] font-semibold text-foreground hover:bg-muted'
  return (
    <div className="flex gap-1.5">
      {phone ? (
        <>
          <a href={`sms:${phone}`} className={linkClass}>
            <MessageSquare className="size-3.5" aria-hidden />
            Text
          </a>
          <a href={`tel:${phone}`} className={linkClass}>
            <Phone className="size-3.5" aria-hidden />
            Call
          </a>
        </>
      ) : null}
      {email ? (
        <a href={`mailto:${email}`} className={linkClass}>
          <Mail className="size-3.5" aria-hidden />
          Email
        </a>
      ) : null}
    </div>
  )
}

export function BookingActions({ event, onChanged, children, className, style, dragProps }: BookingActionsProps) {
  const navigate = useNavigate()
  const { tenantConfig } = useRole()
  const timezone = tenantConfig?.timezone ?? 'UTC'
  const { copy: copyClient, available: canCopyClient } = useCopiedClient()

  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<'document' | 'cancel' | null>(null)
  const [payOpen, setPayOpen] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [checkInOpen, setCheckInOpen] = useState(false)
  const [checkOutOpen, setCheckOutOpen] = useState(false)
  const [rescheduleOpen, setRescheduleOpen] = useState(false)
  const [notesOpen, setNotesOpen] = useState(false)
  const [intakeOpen, setIntakeOpen] = useState(false)
  const [cardOpen, setCardOpen] = useState(false)

  // Who this is, and what they hold. Fetched when the sheet OPENS rather than with
  // the calendar: this is one customer, so there is no N+1 — and doing it per tile
  // across a full day would be exactly that.
  const [customer, setCustomer] = useState<CustomerDetail | null>(null)
  const [serviceId, setServiceId] = useState<string | null>(null)
  const [detailError, setDetailError] = useState<string | null>(null)
  // THE LAST SETTINGS, which is what the operator opens a booking to see. Read
  // when the sheet opens, from the client's own notes — a completed treatment
  // card is a note, so this is the same history the Notes tab shows.
  const [lastCard, setLastCard] = useState<CustomerNote | null>(null)
  const [cardReload, setCardReload] = useState(0)

  useEffect(() => {
    if (!open) return
    let live = true
    setDetailError(null)
    if (event.customer_id) {
      getCustomer(event.customer_id)
        .then((c) => { if (live) setCustomer(c) })
        .catch((e) => { if (live) setDetailError(errorReason(e)) })
    }
    // The booking's own service, for Move. CalendarEvent carries the service NAME
    // but not its id, and reschedule needs the id to ask what is free.
    getBooking(event.id)
      .then((b) => { if (live) setServiceId(b.service_id) })
      .catch(() => { /* Move stays disabled; the sheet is still useful */ })
    // The most recent treatment card for this client. cards_only + limit 1, so
    // it is one bounded row rather than their whole notes history — and it is
    // deliberately not narrowed to one CARD here: the sheet shows what was last
    // recorded, whichever card it was on, and the dialog does the per-card
    // carry-forward once the operator has chosen one.
    if (event.customer_id) {
      getCustomerNotes(event.customer_id, { cardsOnly: true, limit: 1 })
        .then((page) => { if (live) setLastCard(page.items[0] ?? null) })
        .catch(() => { /* the row still opens the dialog */ })
    }
    return () => { live = false }
  }, [open, event.id, event.customer_id, cardReload])

  const attendance = attendanceState(event)
  const paid = event.payment_status === 'paid'
  // A cancelled or superseded booking has no session to pay for — the API
  // rejects it with a 409, so the sheet shouldn't offer it either.
  const settleable = event.status !== 'cancelled' && event.status !== 'rescheduled'
  const canPay = !paid && settleable && event.payment_status !== undefined
  // Both attendance moves need a live booking. Check-in additionally needs them
  // not to have arrived already; check-out needs them not to be finished.
  const canCheckIn = event.status === 'booked' && attendance === 'not_arrived'
  const canCheckOut = event.status === 'booked'
  const canEmailDocument = settleable
  // The API refuses both once someone has checked in — see the route guards.
  const canCancel = event.status === 'booked' && attendance === 'not_arrived'
  const canReschedule = canCancel && serviceId !== null
  const documentIsReceipt = paid || event.payment_status === undefined

  const dob = customer?.profile?.dob ?? null
  const birthday = birthdayLabel(dob, todayLocalDate(timezone))
  // Only credits that still have something in them — a spent package is history,
  // and listing it here reads as available balance.
  const credits = (customer?.package_balances ?? []).filter((p) => p.sessions_remaining > 0)

  async function handleEmailDocument() {
    if (busy) return
    setBusy('document')
    try {
      const result = await issueBookingDocument(event.id)
      if (result.sent) {
        toast.success(
          `${result.invoice.title ?? 'Document'} ${result.invoice.document_number} sent`,
          { description: `Emailed to ${result.invoice.sent_to}.` },
        )
      } else {
        // Issued but undelivered. The number is spent and the record exists, so
        // say which document it was rather than implying nothing happened.
        toast.error(`${result.invoice.document_number} could not be emailed`, {
          description: result.send_error ?? 'The email was not accepted.',
        })
      }
      onChanged?.()
    } catch (e) {
      toast.error("Couldn't send the document", { description: errorReason(e) })
    } finally {
      setBusy(null)
    }
  }

  async function handleCancel(reason: string) {
    if (busy) return
    setBusy('cancel')
    try {
      await cancelBooking(event.id, reason.trim() || undefined)
      // The dialog closes on the way out rather than on the click, so a failure
      // leaves the operator looking at the thing they asked for with the reason
      // on screen, instead of back at a tile that silently did not change.
      setCancelOpen(false)
      setOpen(false)
      toast.success(`${event.customer_name ?? 'Appointment'} cancelled`, {
        description: `${event.service_name} · ${formatTime(event.start)}. The slot is free again.`,
      })
      onChanged?.()
    } catch (e) {
      toast.error("Couldn't cancel", { description: errorReason(e) })
    } finally {
      setBusy(null)
    }
  }

  const payLabel = paid
    ? (event.paid_with_package ? 'Paid by package' : 'Paid')
    : !settleable ? 'Nothing to pay'
    : event.payment_status === 'partial' ? 'Part paid — settle'
    : 'Mark paid'

  const documentLabel = !canEmailDocument
    ? 'Nothing to invoice'
    : documentIsReceipt ? 'Email receipt' : 'Email invoice'

  const attendanceLabel =
    attendance === 'checked_out' ? 'Checked out'
    : attendance === 'in_session' ? 'In session'
    : event.status === 'booked' ? 'Not arrived'
    : event.status.replace(/_/g, ' ')

  return (
    <>
      <button
        type="button"
        className={cn('cursor-pointer text-left', className)}
        style={style}
        onClick={() => setOpen(true)}
        {...dragProps}
        aria-label={`Actions for ${event.customer_name ?? 'booking'} at ${formatTime(event.start)}`}
      >
        {children}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] max-w-md overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-1.5">
              <span className="min-w-0 truncate">{event.customer_name ?? 'Booking'}</span>
              {/* Only during the birthday week, seven days either side. The emoji
                  carries a title and a label — never colour or a glyph alone. */}
              {birthday ? (
                <span title={birthday} aria-label={birthday} className="shrink-0">
                  <CakeSlice className="size-4 text-pink-600" aria-hidden />
                </span>
              ) : null}
            </DialogTitle>
            <DialogDescription>
              {event.service_name} · {formatTime(event.start)} ·{' '}
              {durationMins(event.start, event.end)}min · {attendanceLabel}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3.5">
            {/* ── Who they are ────────────────────────────────────────────── */}
            <div className="flex flex-col gap-1.5">
              {birthday ? (
                <p className="text-[12.5px] font-semibold text-pink-700 dark:text-pink-400">
                  {birthday}
                </p>
              ) : null}

              {credits.length > 0 ? (
                <div className="flex flex-col gap-0.5">
                  {credits.map((c) => (
                    <span key={c.customer_package_id} className="text-[12.5px] text-foreground">
                      <span className="font-semibold">{c.package_name ?? 'Package'}</span>
                      {': '}
                      {c.sessions_used}/{c.sessions_total} used ·{' '}
                      {c.sessions_remaining} left
                    </span>
                  ))}
                </div>
              ) : customer ? (
                <p className="text-[12px] text-muted-foreground">No package credits left.</p>
              ) : null}

              {detailError ? (
                <p className="text-[11.5px] text-muted-foreground">
                  Couldn't load their record — the actions below still work.
                </p>
              ) : null}

              <ContactRow
                phone={customer?.phone ?? null}
                email={customer?.email ?? null}
              />
            </div>

            {/* ── Attendance ─────────────────────────────────────────────── */}
            <div className="flex flex-col gap-1.5 border-t border-border pt-3">
              <ActionRow
                icon={<LogIn className="size-4" aria-hidden />}
                label={event.checked_in_at ? 'Checked in' : 'Check in'}
                hint={
                  attendance === 'not_arrived'
                    ? 'Mark arrived and deduct the right credit'
                    : event.checked_in_at
                      ? `Arrived ${formatTime(event.checked_in_at)}`
                      : undefined
                }
                disabled={!canCheckIn}
                onClick={() => setCheckInOpen(true)}
              />

              <ActionRow
                icon={<CircleCheck className="size-4" aria-hidden />}
                label={attendance === 'checked_out' ? 'Checked out' : 'Check out'}
                hint={
                  attendance === 'checked_out'
                    ? event.checked_out_at
                      ? `Finished ${formatTime(event.checked_out_at)}`
                      : 'Session completed'
                    : 'Complete the session and record the finishing time'
                }
                disabled={!canCheckOut}
                onClick={() => setCheckOutOpen(true)}
              />
            </div>

            {/* ── Money & documents ──────────────────────────────────────── */}
            <div className="flex flex-col gap-1.5 border-t border-border pt-3">
              <ActionRow
                icon={
                  paid
                    ? <CircleDollarSign className="size-4 text-emerald-600" aria-hidden />
                    : <CircleDollarSignOff className="size-4 text-red-600" aria-hidden />
                }
                label={payLabel}
                hint={!paid && settleable ? 'A package credit, or a card payment link' : undefined}
                disabled={!canPay}
                onClick={() => setPayOpen(true)}
              />

              <ActionRow
                icon={
                  documentIsReceipt
                    ? <Receipt className="size-4" aria-hidden />
                    : <FileText className="size-4" aria-hidden />
                }
                label={documentLabel}
                disabled={!canEmailDocument || busy !== null}
                busy={busy === 'document'}
                onClick={() => void handleEmailDocument()}
              />
            </div>

            {/* ── Moving it, and going elsewhere ─────────────────────────── */}
            <div className="flex flex-col gap-1.5 border-t border-border pt-3">
              <ActionRow
                icon={<CalendarClock className="size-4" aria-hidden />}
                label="Move to another time"
                hint={
                  attendance !== 'not_arrived'
                    ? 'Not once they have arrived'
                    : 'Same treatment, a real open slot'
                }
                disabled={!canReschedule}
                onClick={() => setRescheduleOpen(true)}
              />

              {event.customer_id ? (
                <ActionRow
                  icon={<ClipboardList className="size-4" aria-hidden />}
                  label="Intake form"
                  // The hint reads the /schedule day-sheet enrichment when the
                  // event carries it; the dialog fetches its own state, so on a
                  // surface with no enrichment the row still works.
                  hint={
                    event.clearance_pending
                      ? 'Medical clearance pending'
                      : event.intake_status === 'complete' ? 'Completed'
                      : event.intake_status === 'pending' ? 'Sent — not answered yet'
                      : event.intake_status === 'not_sent' ? 'Not sent'
                      : 'Send, open, or copy the link'
                  }
                  onClick={() => setIntakeOpen(true)}
                />
              ) : null}

              {event.customer_id ? (
                <ActionRow
                  icon={<ClipboardPen className="size-4" aria-hidden />}
                  label="Treatment card"
                  // The LAST SETTINGS in the hint, which is what the operator
                  // opens a booking to see — the same glance-without-opening
                  // the Client notes row gives. The dialog then carries them
                  // forward into today's card.
                  hint={
                    lastCard
                      ? cardSummary(lastCard.card_snapshot, lastCard.answers) || 'Nothing was filled in last time'
                      : customer ? 'Nothing recorded yet' : undefined
                  }
                  onClick={() => setCardOpen(true)}
                />
              ) : null}

              {event.customer_id ? (
                <ActionRow
                  icon={<NotebookPen className="size-4" aria-hidden />}
                  label="Client notes"
                  // The last entered note rides in the hint, as asked — one
                  // glance says what was written without opening anything.
                  hint={
                    customer
                      ? (customer.recent_notes?.[0]?.note ? notePlainText(customer.recent_notes[0].note) : 'No notes yet')
                      : undefined
                  }
                  onClick={() => setNotesOpen(true)}
                />
              ) : null}

              {event.customer_id && event.customer_name && canCopyClient ? (
                <ActionRow
                  icon={<ClipboardCopy className="size-4" aria-hidden />}
                  label="Copy client"
                  hint="Then tap an empty slot to book them again"
                  onClick={() => {
                    copyClient({ id: event.customer_id!, name: event.customer_name! })
                    toast.success(`${event.customer_name} copied`, {
                      description: 'Tap an empty slot to book them again.',
                    })
                    setOpen(false)
                  }}
                />
              ) : null}

              <ActionRow
                icon={<SquareArrowOutUpRight className="size-4" aria-hidden />}
                label="Open booking"
                onClick={() => navigate(`/booking/${event.id}`)}
              />

              {event.customer_id ? (
                <ActionRow
                  icon={<CircleUser className="size-4" aria-hidden />}
                  label="Open customer"
                  onClick={() =>
                    navigate(`/customer/${event.customer_id}${event.customer_name ? `?name=${encodeURIComponent(event.customer_name)}` : ''}`)
                  }
                />
              ) : null}
            </div>

            {/* ── The one thing that cannot be undone ────────────────────── */}
            <div className="border-t border-border pt-3">
              <ActionRow
                icon={<CalendarX2 className="size-4" aria-hidden />}
                label={
                  canCancel
                    ? 'Cancel appointment'
                    : event.status === 'cancelled'
                      ? 'Cancelled'
                      : event.checked_in_at
                        ? 'Already checked in'
                        : `Can't cancel · ${event.status.replace(/_/g, ' ')}`
                }
                hint={canCancel ? 'The customer told you they are not coming' : undefined}
                tone="destructive"
                disabled={!canCancel || busy !== null}
                onClick={() => setCancelOpen(true)}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/*
        Every sub-dialog mounts OUTSIDE the sheet's content. Radix unmounts a
        dialog's children when it closes, so a nested dialog would go with it.
      */}
      <PaymentDialog
        bookingId={payOpen ? event.id : null}
        open={payOpen}
        onOpenChange={setPayOpen}
        onChanged={onChanged}
      />

      <CheckInDialog
        event={checkInOpen ? event : null}
        onOpenChange={(next) => { setCheckInOpen(next); if (!next) setOpen(false) }}
        onChanged={onChanged}
      />

      <CheckOutDialog
        event={checkOutOpen ? event : null}
        timezone={timezone}
        onOpenChange={(next) => { setCheckOutOpen(next); if (!next) setOpen(false) }}
        onChanged={onChanged}
      />

      <RescheduleDialog
        event={rescheduleOpen ? event : null}
        serviceId={serviceId}
        onOpenChange={(next) => { setRescheduleOpen(next); if (!next) setOpen(false) }}
        onChanged={onChanged}
      />

      <CancelBookingDialog
        event={event}
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        busy={busy === 'cancel'}
        onConfirm={handleCancel}
      />

      <ClientNotesDialog
        customer={
          notesOpen && event.customer_id
            ? { id: event.customer_id, name: event.customer_name ?? 'Customer' }
            : null
        }
        bookingId={event.id}
        onOpenChange={setNotesOpen}
      />

      <TreatmentCardDialog
        customer={cardOpen && event.customer_id
          ? { id: event.customer_id, name: event.customer_name ?? 'this client' }
          : null}
        bookingId={event.id}
        serviceName={event.service_name}
        onOpenChange={(v) => setCardOpen(v)}
        // Re-read the last card so the hint shows what was just saved, rather
        // than the session before it.
        onSaved={() => { setCardReload((n) => n + 1); onChanged?.() }}
      />

      <IntakeDialog
        onViewAnswers={(submissionId) => navigate(`/intake-submission/${submissionId}`)}
        customer={
          intakeOpen && event.customer_id
            ? { id: event.customer_id, name: event.customer_name ?? 'Customer' }
            : null
        }
        bookingId={event.id}
        serviceId={serviceId}
        onOpenChange={setIntakeOpen}
        onChanged={onChanged}
      />
    </>
  )
}

/**
 * Confirms a cancellation before it happens, because nothing here can put it
 * back: the API has no un-cancel route, so recovering means booking the slot
 * again — and the cancellation is also counted against the customer
 * (`cancellation_count`), which is a record the business reads later.
 *
 * The reason is OPTIONAL and stays optional. Demanding an explanation before an
 * operator can free a slot would make the fast path slow for the common case
 * ("she called, she's sick"), and a required field invites "n/a".
 *
 * The dismiss button says "Keep appointment", never "Cancel": in a dialog about
 * cancelling, a button labelled Cancel means both things at once.
 */
function CancelBookingDialog({
  event,
  open,
  onOpenChange,
  busy,
  onConfirm,
}: {
  event: CalendarEvent
  open: boolean
  onOpenChange: (open: boolean) => void
  busy: boolean
  onConfirm: (reason: string) => void
}) {
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (open) setReason('')
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Cancel this appointment?</DialogTitle>
          <DialogDescription>
            {event.customer_name ?? 'This customer'} · {event.service_name} ·{' '}
            {formatTime(event.start)}, {durationMins(event.start, event.end)}min
          </DialogDescription>
        </DialogHeader>

        <p className="text-[12.5px] text-muted-foreground">
          The slot goes back on the calendar for someone else to take, and this counts towards the
          customer’s cancellation record.
        </p>
        <p className="text-[12.5px] text-muted-foreground">
          There is no undo — putting it back means making the booking again. If the customer simply
          never turned up and never told you, that is a no-show, not a cancellation: ask in chat to
          mark it.
        </p>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
            Reason (optional)
          </span>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Called in sick…"
            className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-[14px] text-foreground outline-none placeholder:text-muted-foreground focus:border-primary"
          />
        </label>

        <div className="mt-1 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="rounded-md border border-border bg-card px-3 py-1.5 text-[12.5px] font-semibold text-foreground hover:bg-muted disabled:opacity-40"
          >
            Keep appointment
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason)}
            disabled={busy}
            className="flex items-center gap-1.5 rounded-md bg-rose-700 px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-rose-800 disabled:opacity-40"
          >
            {busy ? <Loader2 size={14} className="animate-spin" aria-hidden /> : <CalendarX2 size={14} aria-hidden />}
            Cancel appointment
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * One wrapper for all three calendar views. Interactive tiles become a sheet
 * trigger; everything else keeps the click-through-to-booking behaviour of a
 * plain tile (a no-op for customers and synthetic ids — see useOpenBooking),
 * so a customer-facing calendar renders byte-identically to before.
 *
 * DRAG-TO-MOVE LIVES HERE, not in the two day views, and that is deliberate.
 * `AttendanceMark` was added to one day grid and not the other, so the same
 * booking showed a tick on one and nothing on the other for months. A gesture
 * that moves a real appointment is a far worse thing to have in one grid and not
 * the other, so both get it from this one place or neither does.
 */
export function EventShell({
  event,
  interactive,
  onChanged,
  className,
  style,
  children,
  move,
}: {
  event: CalendarEvent
  interactive?: boolean
  onChanged?: () => void
  className?: string
  style?: CSSProperties
  children: ReactNode
  /**
   * Grid geometry + the proposal callback. Absent means no dragging — which is
   * every chat calendar, every non-interactive surface, and the week view (a list
   * of cards with no time axis, so a drag there would mean nothing).
   */
  move?: TileMoveGeometry
}) {
  const openBooking = useOpenBooking()

  // The hook is called unconditionally — hooks cannot be conditional — and
  // `enabled` carries the whole decision. `canDragBooking` asks exactly what
  // `PATCH /:id/reschedule` enforces: booked, and nobody has arrived yet.
  const draggable = Boolean(interactive && move) && canDragBooking(event)
  const drag = useTileDrag({
    enabled: draggable,
    originalStartMinutes: minutesFromLocalTime(event.start),
    durationMinutes: durationMins(event.start, event.end),
    pixelsPerMinute: move?.pixelsPerMinute ?? 1,
    intervalMinutes: move?.intervalMinutes ?? 30,
    dayStartMinutes: move?.dayStartMinutes ?? 0,
    dayEndMinutes: move?.dayEndMinutes ?? 24 * 60,
    onDrop: (startMinutes) => {
      if (!move) return
      move.onMoveBooking({ event, date: move.date, startMinutes })
    },
  })

  if (!interactive) {
    const link = tileLinkProps(event, openBooking)
    return (
      <div
        {...link}
        className={cn(
          className,
          link.onClick && 'cursor-pointer transition-[filter] hover:brightness-95 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
        )}
        style={style}
      >
        {children}
      </div>
    )
  }

  // While a drag is in flight the tile is lifted out of the paint order and
  // shifted by the SNAPPED offset, so it sits on the slot it would land on.
  // `transform` rather than `top`: it does not invalidate layout, so a 30-band
  // column does not reflow on every pointer move.
  const dragStyle: CSSProperties | undefined = drag.state.dragging
    ? {
        ...style,
        transform: `translateY(${drag.state.offsetPixels}px)`,
        zIndex: 30,
        opacity: 0.9,
        cursor: 'grabbing',
        // The tile must not intercept its own pointer while being dragged, or a
        // fast drag over it re-targets and the gesture stutters.
        touchAction: 'none',
      }
    : draggable
      ? { ...style, cursor: 'grab', touchAction: 'manipulation' }
      : style

  return (
    <BookingActions
      event={event}
      onChanged={onChanged}
      className={className}
      style={dragStyle}
      dragProps={draggable ? { onPointerDown: drag.onPointerDown, onClickCapture: drag.onClickCapture } : undefined}
    >
      {children}
      {drag.state.dragging && drag.state.targetMinutes !== null ? (
        // The proposed time, on the tile, while it is being dragged. Without it
        // the operator is judging a 15-minute step by eye against a half-hour
        // grid — and the confirm dialog would be the first place the real answer
        // appeared.
        <span className="pointer-events-none absolute inset-x-0 bottom-0 truncate bg-primary px-1 py-px text-center text-[10.5px] font-bold text-primary-foreground">
          {minutesToLabel(drag.state.targetMinutes)}
        </span>
      ) : null}
    </BookingActions>
  )
}
